import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { CalendarCheck, Plus, Trash2, Hand, Check, ChevronLeft, ChevronRight } from "lucide-react";

const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); const p = (n) => String(n).padStart(2, "0"); return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`; };
const KIND = [{ k: "unavailable", l: "Μη διαθέσιμος" }, { k: "available", l: "Διαθέσιμος" }, { k: "preferred", l: "Προτίμηση" }];
const DOW = ["Δευ", "Τρί", "Τετ", "Πέμ", "Παρ", "Σάβ", "Κυρ"];

export default function MyAvailability() {
  const qc = useQueryClient();
  const [week, setWeek] = useState(mondayOf(new Date()));
  const [memberId, setMemberId] = useState("");
  const [form, setForm] = useState({ kind: "unavailable", recurring: false, date: "", weekday: 0, start: "", end: "", note: "" });

  const { data: sched } = useQuery({ queryKey: ["ma-sched", week], queryFn: async () => (await api.get(`/team/schedule?week_start=${week}`)).data });
  const { data: avail = [] } = useQuery({ queryKey: ["ma-avail"], queryFn: async () => (await api.get("/team/availability")).data });

  const myMemberId = sched?.my_member_id;
  const isManager = ["account_owner", "general_manager", "manager"].includes(sched?.access_level);
  const needMember = isManager && !myMemberId;
  const unlinked = !isManager && !myMemberId;
  const effMember = myMemberId || memberId;
  const members = sched?.members || [];
  const openShifts = (sched?.shifts || []).filter((s) => s.is_open);
  const shiftWeek = (n) => { const d = new Date(week); d.setDate(d.getDate() + n); setWeek(mondayOf(d)); };

  const addAvail = useMutation({
    mutationFn: async () => {
      const body = { kind: form.kind, recurring: form.recurring, start: form.start, end: form.end, note: form.note, member_id: effMember };
      if (form.recurring) body.weekday = Number(form.weekday); else body.date = form.date;
      return (await api.post("/team/availability", body)).data;
    },
    onSuccess: () => { toast.success("Καταχωρήθηκε"); setForm({ ...form, date: "", note: "" }); qc.invalidateQueries({ queryKey: ["ma-avail"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const delAvail = useMutation({ mutationFn: async (id) => (await api.delete(`/team/availability/${id}`)).data, onSuccess: () => { toast.success("Διαγράφηκε"); qc.invalidateQueries({ queryKey: ["ma-avail"] }); }, onError: (e) => toast.error(apiErr(e)) });
  const claim = useMutation({ mutationFn: async (shiftId) => (await api.post(`/team/shifts/${shiftId}/claim`, { member_id: effMember })).data, onSuccess: () => { toast.success("Το αίτημα στάλθηκε"); qc.invalidateQueries({ queryKey: ["ma-sched"] }); }, onError: (e) => toast.error(apiErr(e)) });
  const ack = useMutation({ mutationFn: async () => (await api.post("/team/schedule/ack", { week_start: week, member_id: effMember })).data, onSuccess: () => { toast.success("Επιβεβαιώθηκε"); qc.invalidateQueries({ queryKey: ["ma-sched"] }); }, onError: (e) => toast.error(apiErr(e)) });

  const needMember2 = needMember;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="my-availability-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Team &amp; Time</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><CalendarCheck className="h-7 w-7" /> Διαθεσιμότητα & Βάρδιες</h1>
      </div>

      {unlinked && (
        <div className="rounded-xl bg-[#F3EADF] border border-[#E7D9C4] p-3 text-sm text-[#9A6A3A]" data-testid="ma-unlinked">
          Ο λογαριασμός σου δεν συνδέεται ακόμη με μέλος ομάδας. Ζήτησε από τον διευθυντή να συνδέσει το email σου για να δηλώσεις διαθεσιμότητα.
        </div>
      )}

      {needMember2 && (
        <div className="rounded-xl bg-[#F3EADF] border border-[#E7D9C4] p-3 text-sm">
          <label className="block text-[#9A6A3A] font-semibold mb-1">Ενεργείς ως διευθυντής — επίλεξε μέλος:</label>
          <select data-testid="ma-member" value={memberId} onChange={(e) => setMemberId(e.target.value)} className="w-full rounded-xl border border-[#E3DED4] px-3 py-2 bg-white">
            <option value="">—</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button data-testid="ma-prev" onClick={() => shiftWeek(-7)} className="h-9 w-9 rounded-xl border border-[#E3DED4] bg-white flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
        <span className="text-sm text-[#45423C] font-medium">{sched?.week_start} – {sched?.week_end}</span>
        <button data-testid="ma-next" onClick={() => shiftWeek(7)} className="h-9 w-9 rounded-xl border border-[#E3DED4] bg-white flex items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
        {sched?.status === "published" && (
          sched?.my_ack ? <span className="ml-auto text-sm text-[#3F6B4C] inline-flex items-center gap-1"><Check className="h-4 w-4" /> Επιβεβαιωμένο</span>
            : <button data-testid="ack-btn" disabled={!effMember} onClick={() => ack.mutate()} className="ml-auto rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50">Είδα το πρόγραμμα</button>
        )}
      </div>

      <section className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-4" data-testid="avail-form">
        <h2 className="font-serif text-xl text-[#45423C]">Δήλωση διαθεσιμότητας</h2>
        <div className="flex flex-wrap gap-2">
          {KIND.map((k) => (
            <button key={k.k} data-testid={`kind-${k.k}`} onClick={() => setForm({ ...form, kind: k.k })} className={`px-4 py-2 rounded-xl text-sm font-semibold border ${form.kind === k.k ? "bg-[#6E7A63] text-white border-[#6E7A63]" : "border-[#E3DED4] text-[#6F6A60] bg-white"}`}>{k.l}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-[#45423C]"><input type="checkbox" data-testid="avail-recurring" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} /> Επαναλαμβανόμενο (εβδομαδιαίο)</label>
        <div className="grid sm:grid-cols-2 gap-3">
          {form.recurring ? (
            <div className="flex flex-wrap gap-1.5">
              {DOW.map((d, i) => <button key={i} data-testid={`dow-${i}`} onClick={() => setForm({ ...form, weekday: i })} className={`px-3 py-1.5 rounded-lg text-sm border ${form.weekday === i ? "bg-[#45423C] text-white border-[#45423C]" : "border-[#E3DED4] text-[#6F6A60]"}`}>{d}</button>)}
            </div>
          ) : (
            <input data-testid="avail-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm" />
          )}
          <div className="flex items-center gap-2">
            <input data-testid="avail-start" type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="rounded-xl border border-[#E3DED4] px-2 py-2 text-sm" />
            <span className="text-[#8A8577]">–</span>
            <input data-testid="avail-end" type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="rounded-xl border border-[#E3DED4] px-2 py-2 text-sm" />
            <span className="text-[11px] text-[#8A8577]">(κενό = όλη μέρα)</span>
          </div>
        </div>
        <input data-testid="avail-note" placeholder="Σημείωση" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm" />
        <button data-testid="avail-submit" disabled={!effMember || (!form.recurring && !form.date) || addAvail.isPending} onClick={() => addAvail.mutate()} className="rounded-xl bg-[#6E7A63] text-white px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"><Plus className="h-4 w-4" /> Καταχώρηση</button>
      </section>

      <section data-testid="avail-list">
        <h2 className="font-serif text-xl text-[#45423C] mb-3">Οι δηλώσεις μου</h2>
        <div className="space-y-2">
          {avail.map((a) => (
            <div key={a.id} data-testid={`myavail-${a.id}`} className="rounded-xl bg-white border border-[#E3DED4] px-4 py-3 flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium text-[#45423C]">{KIND.find((k) => k.k === a.kind)?.l}</span>
                <span className="text-[#8A8577] ml-2">{a.recurring ? `κάθε ${DOW[a.weekday]}` : a.date}{a.start ? ` · ${a.start}–${a.end}` : " · όλη μέρα"}{a.note ? ` · ${a.note}` : ""}</span>
                <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full ${a.status === "approved" ? "bg-[#E2ECE4] text-[#3F6B4C]" : a.status === "rejected" ? "bg-[#F6E2DD] text-[#A1493A]" : "bg-[#F3EADF] text-[#9A6A3A]"}`}>{a.status === "approved" ? "εγκρίθηκε" : a.status === "rejected" ? "απορρίφθηκε" : "εκκρεμεί"}</span>
              </div>
              <button data-testid={`myavail-del-${a.id}`} onClick={() => delAvail.mutate(a.id)} className="h-8 w-8 rounded-lg border border-[#E7CEC6] text-[#A1493A] flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {avail.length === 0 && <p className="text-sm text-[#6F6A60]">Καμία δήλωση.</p>}
        </div>
      </section>

      <section data-testid="open-shifts">
        <h2 className="font-serif text-xl text-[#45423C] mb-3">Ανοιχτές βάρδιες αυτής της εβδομάδας</h2>
        <div className="space-y-2">
          {openShifts.map((s) => (
            <div key={s.id} data-testid={`open-shift-${s.id}`} className="rounded-xl bg-white border border-[#E3DED4] px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-[#45423C]">{(s.date || "").slice(5)} · {(s.start || "").slice(0, 5)}–{(s.end || "").slice(0, 5)}{s.role_name ? ` · ${s.role_name}` : ""}</div>
              <button data-testid={`claim-btn-${s.id}`} disabled={!effMember} onClick={() => claim.mutate(s.id)} className="rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"><Hand className="h-4 w-4" /> Διεκδίκηση</button>
            </div>
          ))}
          {openShifts.length === 0 && <p className="text-sm text-[#6F6A60]">Καμία ανοιχτή βάρδια (δημοσιευμένη).</p>}
        </div>
      </section>
    </div>
  );
}
